import { Grid, Theme } from "@mui/material";
import { IReportWebdatarocks, ReportUrlType } from "@vmosolutions/oc-interfaces";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import * as WebDataRocksReact from "react-webdatarocks";
import AmplifyAPIWrapper, { AmplifyEndpoint, INotifications } from "src/API/AmplifyAPIWrapper";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import { updateReportNodes } from "src/redux/actions/Reporting/Reporting.Actions";
import { isDevEnv } from "src/utils/index";

export type IWebDataReport = IReportWebdatarocks & WebDataRocks.Report;

interface IWDRPivotTableProps {
  report?: IWebDataReport;
}

/**
 * Check for existance of webdatarocks css in the head links. If found it means custom theme is applied.
 * @param switchTheme - if true, remove this link and revert back to default theme.
 * @returns
 */
const themeIsLight = (switchTheme?: boolean): boolean => {
  const linkTags: HTMLCollectionOf<HTMLLinkElement> = document.head.getElementsByTagName("link");
  for (let i = 0; i < linkTags.length; i++) {
    if (linkTags[i].href.indexOf("webdatarocks.min.css") > -1) {
      if (switchTheme) {
        document.head.removeChild(linkTags[i]);
      }
      return true;
    }
  }
  return false;
};

/**
 * Remove custom theme (light) or add it in order to toggle color of grid.
 */
const toggleThemeHandler = (): void => {
  const themeIsCustom = themeIsLight(true);
  if (!themeIsCustom) {
    // const newTheme = "https://cdn.webdatarocks.com/latest/webdatarocks.min.css";
    // File moved into public folder to remove dependency on CDN repo.
    const newTheme = "/assets/webdatarocks/webdatarocks.min.css";
    const link = document.createElement("link");
    link.href = newTheme;
    link.rel = "stylesheet";
    link.type = "text/css";
    document.head.appendChild(link);
  }
};

/**
 * Set a customized tabs array for the pivot table.
 */
const customizeToolbar = (toolbar: any, reportWithData: IWebDataReport): void => {
  let currentTabs = toolbar.getTabs();
  if (reportWithData?.readonly) {
    // remove save tab if read only.
    currentTabs = currentTabs?.filter((tab: any) => tab.id !== "wdr-tab-save");
  }
  if (!isDevEnv()) {
    // if produciton env, remove connect and open buttons.
    currentTabs = currentTabs?.filter((tab: any) => tab.id !== "wdr-tab-connect" && tab.id !== "wdr-tab-open");
  }
  // Add a Toggle Theme button to the beginning of the buttons.
  currentTabs.unshift({
    id: "wdr-tab-toggle-theme",
    title: "Theme",
    handler: toggleThemeHandler,
    icon: '<svg xmlns="http://www.w3.org/2000/svg" height="36" width="36" viewBox="-6 -6 36 36"><path d="M20 15.31L23.31 12 20 8.69V4h-4.69L12 .69 8.69 4H4v4.69L.69 12 4 15.31V20h4.69L12 23.31 15.31 20H20v-4.69zM12 18V6c3.31 0 6 2.69 6 6s-2.69 6-6 6z"/></svg>',
  });
  toolbar.getTabs = () => currentTabs;
};

/**
 * Retrieve report data using supplied reportURL and display in pivot table.
 * @param props -
 */
export const WDRPivotTable = (props: IWDRPivotTableProps): JSX.Element => {
  const { report } = props;
  const dispatch = useDispatch();

  const [loadingReportData, setLoadingReportData] = useState<boolean>(null);
  const [reportWithData, setReportWithData] = useState<any>(null);

  // Store component reference to access internal methods.
  const webDataRockRef = useRef<WebDataRocksReact.Pivot>();
  const WDRPivot = webDataRockRef?.current?.webdatarocks;
  // useEffect(() => console.log(WDRPivot), [WDRPivot]);

  useEffect(() => {
    if (!report || !dispatch) {
      return;
    }

    const urlType = report?.urlType;
    const reportFullPath = report?.data?.dataSource?.filename ?? report?.data?.dataSource?.url;

    //TODO If api is set to absolute, bypass amplify as it requires a defined endpoint.
    const endpoint =
      urlType === ReportUrlType.api
        ? AmplifyEndpoint.SkytrainApi
        : urlType === ReportUrlType.app
          ? AmplifyEndpoint.Skytrain
          : null;

    const reportPath = reportFullPath.slice(reportFullPath.lastIndexOf(".cloud") + 6);
    const getReportData = async (): Promise<void> => {
      setLoadingReportData(true);
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: `Loading ${report?.name} Report...`,
        successMessage: `${report?.name} Report Successfully loaded`,
        errorMessage: `Error: Unable to Load ${report?.name} Report`,
      };
      const reportData = await AmplifyAPIWrapper.Get(reportPath, notifications, null, endpoint);
      setLoadingReportData(false);
      if (Array.isArray(reportData)) {
        // Set Report Data and change filename to undefined.
        setReportWithData({
          ...report,
          data: {
            ...report.data,
            dataSource: { ...report.dataSource, data: reportData, filename: undefined },
          },
        });
      }
    };
    getReportData();
  }, [report, dispatch]);

  useEffect(() => {
    if (WDRPivot) {
      //@ts-ignore
      WDRPivot.save = async (): Promise<void> => {
        const data = WDRPivot.getReport();
        if (!data || typeof data === "string" || !report?._id) {
          return;
        }
        // Return all but dataSource changes as we don't wan't to save those.
        const { formats, options, slice } = data;
        const reportChanges = {
          "data.formats": formats,
          "data.options": options,
          "data.slice": slice,
        };
        // console.log(report, reportChanges);
        const notifications: INotifications = {
          dispatch: dispatch,
          infoMessage: `Updating ${report?.name} Report...`,
          successMessage: `${report?.name} Report Successfully Updated`,
          errorMessage: `Error: Unable to Update ${report?.name} Report`,
        };
        const newNodes = await AmplifyAPIWrapper.Put(`/reports/${report._id}`, reportChanges, notifications);
        if (Array.isArray(newNodes) && newNodes.length > 0) {
          dispatch(updateReportNodes(newNodes));
        }
      };
    }
  }, [dispatch, report, WDRPivot]);

  const WebDataRocksStyles = (theme: Theme): any => ({
    root: {
      width: "100%",
      height: "100%",
      padding: "10px",
      "& .dialog_spinner_progress": {
        width: "calc(100% - 20px)",
        height: "calc(100% - 20px)",
      },
      "& .wdr-wrapper": {
        width: "100%",
        height: "100%",
        "& #wdr-toolbar-wrapper #wdr-toolbar": {
          background: "transparent",
        },
      },
    },
  });

  if (reportWithData?.data) {
    return (
      <Grid container direction="column" wrap="nowrap" sx={(theme) => WebDataRocksStyles(theme).root}>
        <SpinnerProgress loading={loadingReportData} message={`Loading ${report?.name} report`} />
        <Grid item className="wdr-wrapper">
          <div style={{ height: "calc(100% - 10px)", width: "100%" }}>
            <WebDataRocksReact.Pivot
              ref={webDataRockRef}
              width="100%"
              height="100%"
              toolbar={true}
              report={reportWithData?.data}
              beforetoolbarcreated={(toolbar: any) => customizeToolbar(toolbar, reportWithData)}
            />
          </div>
        </Grid>
      </Grid>
    );
  } else {
    return null;
  }
};
